import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { ProfitOrderDto, ProfitReportDto } from "@hisense/shared";
import {
  Download,
  Eye,
  Printer,
  RefreshCw,
  Search,
  TriangleAlert,
} from "lucide-react";
import {
  useProfitReport,
  useQuotationCustomerOptions,
  useSettings,
} from "@/api/hooks";
import { PageBody } from "@/components/ui/PageHeader";
import { Button } from "@/components/ui/Button";
import { Input, FieldLabel } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { SearchableFilter } from "@/components/ui/SearchableFilter";
import { Modal } from "@/components/ui/Modal";
import { Pagination } from "@/components/ui/Pagination";
import { TableShell, Td, Th, Tr } from "@/components/ui/Table";
import { ProductProfitPanel } from "@/components/profit/ProductProfitPanel";
import { LoadingState, QueryErrorState } from "@/components/ui/States";
import { PeriodFilter, dayToMs } from "@/components/domain/PeriodFilter";
import { formatDate } from "@/domain/datetime";
import { useAuth } from "@/app/auth";
import { useClampListPage, useListQueryState } from "@/lib/listQuery";
import { buildProfitHtml, printProfit } from "@/lib/printProfit";
import { exportProfitCsv } from "@/lib/profitCsv";
import { getLocalizedError } from "@/lib/api";
import { ProfitDetail, CostBadge } from "@/components/profit/ProfitDetail";
import { SaveAsPdfHint } from "@/components/domain/SaveAsPdfHint";
import {
  amount,
  quantities,
  marginClass,
} from "@/components/profit/profitDisplay";

const COST_STATUSES = ["all", "po", "mixed", "catalog", "missing"] as const;
const PROFIT_STATES = [
  "all",
  "profit",
  "loss",
  "breakeven",
  "unknown",
] as const;
const SORTS = [
  "-margin",
  "margin",
  "-date",
  "date",
  "-revenue",
  "cost",
  "-marginPct",
] as const;
function validDay(value: string): boolean {
  if (!value) return true;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const time = dayToMs(value, false);
  if (time == null) return false;
  const date = new Date(time);
  const normalized = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  return normalized === value;
}
export function ProfitPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const list = useListQueryState();
  const [, setSearchParams] = useSearchParams();
  const from = list.searchParams.get("from") ?? "";
  const to = list.searchParams.get("to") ?? "";
  const customerId = list.searchParams.get("customerId") || undefined;
  // Cùng bộ lọc, hai cách nhìn: theo chứng từ (mặc định) và theo mã hàng.
  const view = list.searchParams.get("view") === "product" ? "product" : "order";
  /**
   * PI chỉ-admin: mặc định KHÔNG cộng vào báo cáo. Chỉ quản trị viên mới thấy
   * bộ lọc này; người khác chọn gì máy chủ cũng bỏ qua.
   */
  const scopeParam = list.searchParams.get("adminOnlyScope");
  const adminOnlyScope = scopeParam === "adminOnly" || scopeParam === "all" ? scopeParam : "normal";
  const currency = list.searchParams.get("currency") || undefined;
  const costStatus =
    COST_STATUSES.find(
      (value) => value === list.searchParams.get("costStatus"),
    ) ?? "all";
  const profitState =
    PROFIT_STATES.find(
      (value) => value === list.searchParams.get("profitState"),
    ) ?? "all";
  const sort = SORTS.find((value) => value === list.sort) ?? "-margin";
  const today = new Date();
  const effectiveTo = to || `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
  const invalidRange =
    !validDay(from) || !validDay(to) || Boolean(from && from > effectiveTo);
  const result = useProfitReport(
    {
      from: dayToMs(from, false),
      to: dayToMs(to, true),
      customerId,
      currency,
      q: list.q.trim() || undefined,
      costStatus,
      profitState,
      sort,
      tzOffsetMinutes: new Date().getTimezoneOffset(),
      ...(adminOnlyScope !== "normal" ? { adminOnlyScope } : {}),
    },
    !invalidRange,
  );
  const report = result.data;
  const customers = useQuotationCustomerOptions();
  const settings = useSettings();
  const orders = useMemo(
    () => report?.groups.flatMap((group) => group.lines) ?? [],
    [report],
  );
  const safePage = Math.min(
    list.page,
    Math.max(1, Math.ceil(orders.length / list.pageSize)),
  );
  const pageRows = orders.slice(
    (safePage - 1) * list.pageSize,
    safePage * list.pageSize,
  );
  useClampListPage(
    report && !result.isFetching && !result.error && !invalidRange
      ? orders.length
      : undefined,
    list.clampPage,
  );
  const customerOptions = (customers.data ?? []).map((customer) => ({
    value: customer.id,
    label: customer.companyName,
  }));
  if (
    customerId &&
    !customerOptions.some((customer) => customer.value === customerId)
  )
    customerOptions.push({
      value: customerId,
      label:
        orders.find((order) => order.customerId === customerId)?.customerName ||
        t("profitWorkspace.unavailableCustomer"),
    });
  const [observedCurrencies, setObservedCurrencies] = useState<string[]>([]);
  useEffect(() => {
    if (!report) return;
    setObservedCurrencies((current) => {
      const next = [
        ...new Set([
          ...current,
          ...report.groups.map((group) => group.currency),
        ]),
      ].sort();
      return next.length === current.length ? current : next;
    });
  }, [report]);
  const currencies = [
    ...new Set([
      ...(settings.data?.businessOperations.optionLists.currencies ?? []),
      ...observedCurrencies,
      ...(report?.groups.map((group) => group.currency) ?? []),
      ...(currency ? [currency] : []),
    ]),
  ].sort();
  const [detail, setDetail] = useState<{
    order: ProfitOrderDto;
    generatedAt: number;
  } | null>(null);
  const [preview, setPreview] = useState<{
    report: ProfitReportDto;
    html: string;
  } | null>(null);
  const [outputError, setOutputError] = useState(false);
  const ready = Boolean(
    report && !result.isFetching && !result.error && !invalidRange,
  );
  const canExport = ready && orders.length > 0;
  const output = (action: () => void) => {
    setOutputError(false);
    try {
      action();
    } catch {
      setOutputError(true);
    }
  };
  const reset = () => {
    setSearchParams(new URLSearchParams());
  };
  const resetPeriod = () => {
    const next = new URLSearchParams(list.searchParams);
    next.delete("from");
    next.delete("to");
    next.delete("page");
    setSearchParams(next);
  };
  return (
    <PageBody className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-3xl">
          <h1 className="text-xl font-semibold text-ink">
            {t("profitWorkspace.title")}
          </h1>
          <p className="mt-1 text-sm text-ink-soft">
            {t("profitWorkspace.basis")}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {user?.role === "admin" &&
            (["normal", "adminOnly", "all"] as const).map((v) => (
              <Button
                key={v}
                size="sm"
                variant={adminOnlyScope === v ? "primary" : "secondary"}
                aria-pressed={adminOnlyScope === v}
                onClick={() => list.updateParam("adminOnlyScope", v === "normal" ? "" : v)}
              >
                {t(`profitWorkspace.scope.${v}`)}
              </Button>
            ))}
          {(["order", "product"] as const).map((v) => (
            <Button
              key={v}
              size="sm"
              variant={view === v ? "primary" : "secondary"}
              aria-pressed={view === v}
              onClick={() => list.updateParam("view", v === "order" ? "" : v)}
            >
              {t(`profitWorkspace.view.${v}`)}
            </Button>
          ))}
        </div>
        <Button
          variant="secondary"
          size="sm"
          disabled={result.isFetching || invalidRange}
          onClick={() => void result.refetch()}
        >
          <RefreshCw
            className={`size-4 ${result.isFetching ? "animate-spin" : ""}`}
          />
          {t("common.refresh")}
        </Button>
      </div>
      <section
        className="space-y-3 rounded-md border border-line bg-surface p-4"
        aria-label={t("profitWorkspace.filters")}
      >
        <PeriodFilter
          from={from}
          to={to}
          onChange={list.updateParam}
          onReset={resetPeriod}
          period={ready ? report?.period : undefined}
        />
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-full sm:w-64">
            <FieldLabel htmlFor="profit-search">
              {t("common.search")}
            </FieldLabel>
            <Input
              id="profit-search"
              {...list.searchInput}
              placeholder={t("profitWorkspace.searchPlaceholder")}
              leading={<Search />}
            />
          </div>
          <div>
            <FieldLabel>{t("common.customer")}</FieldLabel>
            <SearchableFilter
              label={t("common.customer")}
              value={customerId ?? ""}
              options={customerOptions}
              allLabel={t("profitWorkspace.allCustomers")}
              onChange={(value) => list.updateParam("customerId", value)}
              isLoading={customers.isLoading}
              error={
                customers.error ? t("profitWorkspace.customerError") : undefined
              }
              onRetry={() => void customers.refetch()}
            />
          </div>
          <div>
            <FieldLabel htmlFor="profit-currency">
              {t("common.currency")}
            </FieldLabel>
            <Select
              id="profit-currency"
              value={currency ?? ""}
              onChange={(event) =>
                list.updateParam("currency", event.target.value)
              }
            >
              <option value="">{t("profitWorkspace.allCurrencies")}</option>
              {currencies.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <FieldLabel htmlFor="profit-cost-status">
              {t("profitWorkspace.coverage")}
            </FieldLabel>
            <Select
              id="profit-cost-status"
              value={costStatus}
              onChange={(event) =>
                list.updateParam("costStatus", event.target.value)
              }
            >
              {COST_STATUSES.map((value) => (
                <option key={value} value={value}>
                  {t(`profitWorkspace.costStatus.${value}`)}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <FieldLabel htmlFor="profit-state">
              {t("profitWorkspace.profitStateLabel")}
            </FieldLabel>
            <Select
              id="profit-state"
              value={profitState}
              onChange={(event) =>
                list.updateParam("profitState", event.target.value)
              }
            >
              {PROFIT_STATES.map((value) => (
                <option key={value} value={value}>
                  {t(`profitWorkspace.profitState.${value}`)}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <FieldLabel htmlFor="profit-sort">
              {t("profitWorkspace.sortLabel")}
            </FieldLabel>
            <Select
              id="profit-sort"
              value={sort}
              onChange={(event) => list.updateParam("sort", event.target.value)}
            >
              {SORTS.map((value) => (
                <option key={value} value={value}>
                  {t(`profitWorkspace.sort.${value}`)}
                </option>
              ))}
            </Select>
          </div>
          <Button variant="ghost" size="sm" onClick={reset}>
            {t("profitWorkspace.resetFilters")}
          </Button>
        </div>
        <div className="flex flex-wrap items-center gap-2 border-t border-line pt-3">
          <Button
            variant="secondary"
            size="sm"
            disabled={!canExport}
            onClick={() =>
              report &&
              output(() => exportProfitCsv(report, t, "page", pageRows))
            }
          >
            <Download className="size-4" />
            {t("profitWorkspace.exportPage")}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={!canExport}
            onClick={() =>
              report && output(() => exportProfitCsv(report, t, "filtered"))
            }
          >
            {t("profitWorkspace.exportFiltered")}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={!canExport}
            onClick={() =>
              report &&
              output(() =>
                setPreview({ report, html: buildProfitHtml(report, t) }),
              )
            }
          >
            <Eye className="size-4" />
            {t("profitWorkspace.preview")}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={!canExport}
            onClick={() => report && output(() => printProfit(report, t))}
          >
            <Printer className="size-4" />
            {t("common.print")}
          </Button>
          <p className="text-xs text-ink-faint">
            {t("profitWorkspace.sortWithinCurrency")}
          </p>
        </div>
        {outputError && (
          <p role="alert" className="text-sm text-danger">
            {t("profitWorkspace.outputError")}
          </p>
        )}
      </section>
      {invalidRange ? (
        <p
          role="alert"
          className="rounded-md border border-danger/30 bg-danger-tint p-4 text-sm text-danger"
        >
          {t("profitWorkspace.invalidRange")}
        </p>
      ) : result.error ? (
        <QueryErrorState
          error={result.error}
          message={getLocalizedError(result.error, t, "profitWorkspace.loadError")}
          forbiddenMessage={t("errors.forbidden")}
          onRetry={() => void result.refetch()}
        />
      ) : result.isLoading || !report ? (
        <LoadingState />
      ) : (
        <>
          {result.isFetching && (
            <p role="status" className="text-sm text-ink-soft">
              {t("profitWorkspace.refreshing")}
            </p>
          )}
          <div
            aria-busy={result.isFetching}
            className={result.isFetching ? "space-y-5 opacity-60" : "space-y-5"}
          >
            <p className="text-xs text-ink-soft">
              {t("profitWorkspace.reportPeriod", {
                from: formatDate(report.period.from),
                to: formatDate(report.period.to),
              })}{" "}
              ·{" "}
              {t("profitWorkspace.generatedAt", {
                at: new Date(report.generatedAt).toLocaleString(),
              })}
            </p>
            {view === "product" ? (
              <ProductProfitPanel
                params={{ from: report.period.from, to: report.period.to, customerId, currency, q: list.q.trim() || undefined }}
                money={amount}
              />
            ) : (
              <>
            {report.groups.map((group) => (
              <section
                key={group.currency}
                className="rounded-md border border-line bg-surface"
                aria-label={t("profitWorkspace.currencySummary", {
                  currency: group.currency,
                })}
              >
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-line px-4 py-3">
                  <h2 className="font-mono text-base font-semibold text-brand">
                    {group.currency}
                  </h2>
                  <span className="font-mono tabular text-xs text-ink-soft">
                    {t("profitWorkspace.orderCount", {
                      count: group.lines.length,
                    })}{" "}
                    · {quantities(group.quantityByUnit)}
                  </span>
                </div>
                <dl className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2 xl:grid-cols-4">
                  <div>
                    <dt className="text-xs text-ink-soft">
                      {t("profitWorkspace.revenue")}
                    </dt>
                    <dd className="mt-2 break-words font-mono tabular text-lg">
                      {amount(group.totalRevenue, group.currency)}
                    </dd>
                    <p className="mt-1 text-xs text-ink-faint">
                      {t("profitWorkspace.allFilteredPis")}
                    </p>
                  </div>
                  <div>
                    <dt className="text-xs text-ink-soft">
                      {t("profitWorkspace.pricedRevenue")}
                    </dt>
                    <dd className="mt-2 break-words font-mono tabular text-lg">
                      {amount(group.pricedRevenue, group.currency)}
                    </dd>
                    <p className="mt-1 text-xs text-ink-faint">
                      {t("profitWorkspace.pricedScope")}
                    </p>
                  </div>
                  <div>
                    <dt className="text-xs text-ink-soft">
                      {t("profitWorkspace.pricedCost")}
                    </dt>
                    <dd className="mt-2 break-words font-mono tabular text-lg">
                      {amount(
                        group.lines.length > group.costIncompleteCount
                          ? group.totalCost
                          : null,
                        group.currency,
                      )}
                    </dd>
                    <p className="mt-1 text-xs text-ink-faint">
                      {t("profitWorkspace.pricedScope")}
                    </p>
                  </div>
                  <div>
                    <dt className="text-xs text-ink-soft">
                      {t("profitWorkspace.pricedMargin")}
                    </dt>
                    <dd
                      className={`mt-2 break-words font-mono tabular text-lg ${marginClass(group.totalMargin)}`}
                    >
                      {amount(
                        group.lines.length > group.costIncompleteCount
                          ? group.totalMargin
                          : null,
                        group.currency,
                      )}
                    </dd>
                    <p
                      className={`mt-1 font-mono tabular text-xs ${marginClass(group.marginPct)}`}
                    >
                      {group.marginPct == null
                        ? t("profitWorkspace.unknownMargin")
                        : `${group.marginPct}%`}{" "}
                      · {t("profitWorkspace.pricedScope")}
                    </p>
                  </div>
                </dl>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-line px-4 py-3 text-xs text-ink-soft">
                  <span>
                    {t("profitWorkspace.coverageCounts", {
                      po: group.costCoverage.withPo,
                      mixed: group.costCoverage.mixed,
                      catalog: group.costCoverage.catalog,
                      missing: group.costCoverage.missing,
                    })}
                  </span>
                  {group.costIncompleteCount > 0 && (
                    <p className="flex items-center gap-2 text-warn">
                      <TriangleAlert className="size-4 shrink-0" />
                      {t("profitWorkspace.incompleteScope", {
                        count: group.costIncompleteCount,
                        revenue: amount(
                          group.incompleteRevenue,
                          group.currency,
                        ),
                      })}
                    </p>
                  )}
                </div>
              </section>
            ))}
            {orders.length === 0 ? (
              <div className="rounded-md border border-line bg-surface px-4 py-12 text-center">
                <p className="text-sm text-ink-soft">{t("common.noMatches")}</p>
                <Button
                  className="mt-3"
                  variant="secondary"
                  size="sm"
                  onClick={reset}
                >
                  {t("profitWorkspace.resetFilters")}
                </Button>
              </div>
            ) : (
              <>
                <TableShell>
                  <caption className="sr-only">
                    {t("profitWorkspace.ledgerCaption")}
                  </caption>
                  <thead>
                    <tr>
                      <Th
                        pin
                        className="w-12 min-w-12 max-w-12"
                        aria-label={t("profitWorkspace.rowNumber")}
                      >
                        #
                      </Th>
                      <Th pin className="left-12">
                        {t("pi.piNo")}
                      </Th>
                      <Th>{t("common.date")}</Th>
                      <Th>{t("common.customer")}</Th>
                      <Th>{t("common.currency")}</Th>
                      <Th>{t("profitWorkspace.linkedPos")}</Th>
                      <Th>{t("common.quantity")}</Th>
                      <Th align="right">{t("profitWorkspace.revenue")}</Th>
                      <Th align="right">{t("profitWorkspace.cost")}</Th>
                      <Th align="right">{t("profitWorkspace.margin")}</Th>
                      <Th align="right">{t("profitWorkspace.marginPct")}</Th>
                      <Th>{t("profitWorkspace.coverage")}</Th>
                      <Th pinRight>{t("common.actions")}</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.map((order, index) => (
                      <Tr
                        key={order.piId}
                        className="group/row hover:bg-row-hover"
                      >
                        <Td
                          pin
                          className="w-12 min-w-12 max-w-12 font-mono tabular text-xs text-ink-faint"
                        >
                          {(safePage - 1) * list.pageSize + index + 1}
                        </Td>
                        <Td
                          pin
                          className="left-12 font-mono tabular font-medium"
                        >
                          <Link
                            className="text-brand underline-offset-4 hover:underline"
                            to={`/proforma-invoices/${order.piId}`}
                          >
                            {order.piNo}
                          </Link>
                        </Td>
                        <Td className="font-mono tabular text-xs text-ink-soft">
                          {formatDate(order.date)}
                        </Td>
                        <Td
                          className="max-w-64 truncate"
                          title={order.customerName || undefined}
                        >
                          {order.customerName || "—"}
                        </Td>
                        <Td className="font-mono">{order.currency}</Td>
                        <Td>
                          <div className="flex max-w-72 flex-wrap gap-x-2 py-2">
                            {order.poRefs.length ? (
                              order.poRefs.map((po) => (
                                <Link
                                  key={po.id}
                                  className="font-mono text-xs text-brand hover:underline"
                                  to={`/purchase-orders/${po.id}`}
                                >
                                  {po.poNo}
                                </Link>
                              ))
                            ) : (
                              <span className="text-ink-faint">—</span>
                            )}
                          </div>
                        </Td>
                        <Td className="font-mono tabular text-xs">
                          {quantities(order.quantityByUnit)}
                        </Td>
                        <Td align="right" className="font-mono tabular">
                          {amount(order.revenue, order.currency)}
                        </Td>
                        <Td align="right" className="font-mono tabular">
                          {amount(order.cost, order.currency)}
                        </Td>
                        <Td
                          align="right"
                          className={`font-mono tabular font-medium ${marginClass(order.margin)}`}
                        >
                          {order.margin == null
                            ? t("profitWorkspace.unknown")
                            : amount(order.margin, order.currency)}
                        </Td>
                        <Td
                          align="right"
                          className={`font-mono tabular ${marginClass(order.margin)}`}
                        >
                          {order.marginPct == null
                            ? "—"
                            : `${order.marginPct}%`}
                        </Td>
                        <Td>
                          <CostBadge status={order.costStatus} />
                        </Td>
                        <Td pinRight>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            disabled={!ready}
                            aria-label={t("profitWorkspace.inspectPi", {
                              pi: order.piNo,
                            })}
                            title={t("profitWorkspace.costDetail")}
                            onClick={() =>
                              setDetail({
                                order,
                                generatedAt: report.generatedAt,
                              })
                            }
                          >
                            <Eye className="size-4" />
                          </Button>
                        </Td>
                      </Tr>
                    ))}
                  </tbody>
                </TableShell>
                <Pagination
                  page={safePage}
                  pageSize={list.pageSize}
                  total={orders.length}
                  disabled={result.isFetching}
                  onPageChange={list.updatePage}
                  onPageSizeChange={list.updatePageSize}
                />
              </>
            )}
            </>
            )}
          </div>
        </>
      )}
      {detail && (
        <ProfitDetail
          order={detail.order}
          generatedAt={detail.generatedAt}
          onClose={() => setDetail(null)}
        />
      )}
      {preview && (
        <Modal
          title={t("profitWorkspace.preview")}
          closeLabel={t("common.close")}
          onClose={() => setPreview(null)}
          className="max-w-6xl"
        >
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-line p-3">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
              <p className="text-xs text-ink-soft">
                {t("profitWorkspace.savedPreview", {
                  at: new Date(preview.report.generatedAt).toLocaleString(),
                })}
              </p>
              <SaveAsPdfHint />
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => output(() => printProfit(preview.report, t))}
            >
              <Printer className="size-4" />
              {t("common.print")}
            </Button>
          </div>
          <iframe
            title={t("profitWorkspace.preview")}
            srcDoc={preview.html}
            sandbox=""
            className="h-[70dvh] min-h-80 w-full bg-surface"
          />
          {outputError && (
            <p role="alert" className="p-3 text-sm text-danger">
              {t("profitWorkspace.outputError")}
            </p>
          )}
        </Modal>
      )}
    </PageBody>
  );
}
